import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DataGridColumn } from '../../interfaces/builder-screen.models';
import { NzMessageService } from 'ng-zorro-antd/message';
import { LanguageServices } from 'src/app/services/LanguageServices';

@Component({
  selector: 'app-column-manager',
  templateUrl: './column-manager.component.html',
  styleUrls: ['./column-manager.component.scss'],
  standalone: false
})
export class ColumnManagerComponent {
  @Input() visible = false;
  @Input() columns: DataGridColumn[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() columnsChange = new EventEmitter<DataGridColumn[]>();

  expandedIndex: number | null = null;

  editingColumn: DataGridColumn | null = null;

  newColumn: Partial<DataGridColumn> = {};
  isAddingNew = false;

  editingSearchEntity = '';
  editingSearchKeyField = '';
  editingSearchValueField = '';
  editingSearchShowKey = false;

  newSearchEntity = '';
  newSearchKeyField = '';
  newSearchValueField = '';
  newSearchShowKey = false;

  editingDropdownOptions: Array<{ value: any; label: string }> = [];
  newDropdownOptions: Array<{ value: any; label: string }> = [];

  constructor(private message: NzMessageService, private ls: LanguageServices) {}

  open() {
    this.visible = true;
    this.visibleChange.emit(true);
  }

  close() {
    this.visible = false;
    this.cancelEdit();
    this.cancelNew();
    this.visibleChange.emit(false);
  }

  save() {
    if (this.editingColumn || this.isAddingNew) {
      this.message.warning(this.ls.instant('builderScreen.msg.column.finishEditFirst'));
      return;
    }
    this.columnsChange.emit([...this.columns]);
    this.close();
  }

  drop(event: CdkDragDrop<DataGridColumn[]>) {
    moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
    if (this.expandedIndex !== null) {
      if (this.expandedIndex === event.previousIndex) {
        this.expandedIndex = event.currentIndex;
      } else if (
        event.previousIndex < this.expandedIndex && event.currentIndex >= this.expandedIndex
      ) {
        this.expandedIndex--;
      } else if (
        event.previousIndex > this.expandedIndex && event.currentIndex <= this.expandedIndex
      ) {
        this.expandedIndex++;
      }
    }
  }

  toggleExpand(index: number) {
    if (this.expandedIndex === index) {
      this.cancelEdit();
      return;
    }
    this.startEdit(this.columns[index], index);
  }

  startEdit(column: DataGridColumn, index: number) {
    if (this.isAddingNew) {
      this.cancelNew();
    }

    this.editingColumn = { ...column };
    this.expandedIndex = index;

    if (column.type === 'search' && column.searchProperties) {
      this.editingSearchEntity = column.searchProperties.entityName || '';
      this.editingSearchKeyField = column.searchProperties.fields?.keyField || '';
      this.editingSearchValueField = column.searchProperties.fields?.valueField || '';
      this.editingSearchShowKey = column.showKey || false;
    } else {
      this.editingSearchEntity = '';
      this.editingSearchKeyField = '';
      this.editingSearchValueField = '';
      this.editingSearchShowKey = false;
    }

    if (column.type === 'dropdown' && column.options) {
      this.editingDropdownOptions = column.options.map(o => ({ ...o }));
    } else {
      this.editingDropdownOptions = [];
    }
  }

  saveEdit() {
    if (!this.editingColumn || this.expandedIndex === null) return;
    if (!this.validateColumn(this.editingColumn)) return;

    this.applyRichTypeProps(this.editingColumn, 'editing');
    this.columns[this.expandedIndex] = { ...this.editingColumn };

    this.editingColumn = null;
    this.expandedIndex = null;
    this.message.success(this.ls.instant('builderScreen.msg.column.updated'));
  }

  cancelEdit() {
    this.editingColumn = null;
    this.expandedIndex = null;
  }

  startAddNew() {
    if (this.editingColumn) {
      this.cancelEdit();
    }

    this.isAddingNew = true;
    this.newColumn = {
      field: `field_${this.columns.length + 1}`,
      header: `Column ${this.columns.length + 1}`,
      type: 'text',
      dataPath: `field_${this.columns.length + 1}`,
      width: '150px',
      sortable: true,
      filterable: false,
      editable: false
    };
  }

  saveNew() {
    if (!this.validateColumn(this.newColumn as DataGridColumn)) return;

    this.applyRichTypeProps(this.newColumn as DataGridColumn, 'new');
    this.columns.push(this.newColumn as DataGridColumn);

    this.newColumn = {};
    this.newDropdownOptions = [];
    this.newSearchEntity = '';
    this.newSearchKeyField = '';
    this.newSearchValueField = '';
    this.newSearchShowKey = false;
    this.isAddingNew = false;
    this.message.success(this.ls.instant('builderScreen.msg.column.added'));
  }

  cancelNew() {
    this.newColumn = {};
    this.isAddingNew = false;
  }

  remove(index: number) {
    const column = this.columns[index];
    if (this.expandedIndex === index) {
      this.cancelEdit();
    } else if (this.expandedIndex !== null && index < this.expandedIndex) {
      this.expandedIndex--;
    }
    this.columns.splice(index, 1);
    this.message.success(this.ls.instant('builderScreen.msg.column.removed', { header: column.header }));
  }

  private validateColumn(column: Partial<DataGridColumn>): boolean {
    if (!column.field || column.field.trim() === '') {
      this.message.error(this.ls.instant('builderScreen.msg.column.fieldRequired'));
      return false;
    }
    if (!column.header || column.header.trim() === '') {
      this.message.error(this.ls.instant('builderScreen.msg.column.headerRequired'));
      return false;
    }
    if (!column.dataPath || column.dataPath.trim() === '') {
      this.message.error(this.ls.instant('builderScreen.msg.column.dataPathRequired'));
      return false;
    }
    const duplicateIndex = this.columns.findIndex(c => c.field === column.field);
    if (duplicateIndex !== -1 && duplicateIndex !== this.expandedIndex) {
      this.message.error(this.ls.instant('builderScreen.msg.column.duplicated', { field: column.field }));
      return false;
    }
    return true;
  }

  onTypeChange(column: Partial<DataGridColumn>) {
    if (column.type === 'search' || column.type === 'dropdown') {
      column.editable = true;
    }
  }

  private applyRichTypeProps(column: DataGridColumn, mode: 'editing' | 'new') {
    if (column.type === 'search') {
      const entity = mode === 'editing' ? this.editingSearchEntity : this.newSearchEntity;
      const keyField = mode === 'editing' ? this.editingSearchKeyField : this.newSearchKeyField;
      const valueField = mode === 'editing' ? this.editingSearchValueField : this.newSearchValueField;
      const showKey = mode === 'editing' ? this.editingSearchShowKey : this.newSearchShowKey;
      column.searchProperties = {
        entityName: entity,
        fields: { keyField, valueField }
      };
      column.showKey = showKey;
    } else {
      delete column.searchProperties;
      delete column.showKey;
      delete column.fieldMappings;
    }

    if (column.type === 'dropdown') {
      const opts = mode === 'editing' ? this.editingDropdownOptions : this.newDropdownOptions;
      column.options = opts.filter(o => o.value !== '' || o.label !== '');
    } else {
      delete column.options;
      delete column.multiple;
      delete column.showSearch;
    }
  }

  addDropdownOption(options: Array<{ value: any; label: string }>) {
    options.push({ value: '', label: '' });
  }

  removeDropdownOption(options: Array<{ value: any; label: string }>, index: number) {
    options.splice(index, 1);
  }

  getTypeIcon(type: string): string {
    switch (type) {
      case 'text': return 'font-size';
      case 'number': return 'calculator';
      case 'date': return 'calendar';
      case 'boolean': return 'check-square';
      case 'search': return 'search';
      case 'dropdown': return 'down-square';
      default: return 'field-string';
    }
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'text': return '#1890ff';
      case 'number': return '#52c41a';
      case 'date': return '#faad14';
      case 'boolean': return '#722ed1';
      case 'search': return '#eb2f96';
      case 'dropdown': return '#13c2c2';
      default: return '#8c8c8c';
    }
  }
}
